import React, { useState, useEffect } from 'react'
import { Form, Input, Button, Table, Space, Row, Col, Modal } from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getActSection,
  saveActSection,
  deleteActSection,
  updateActSection,
} from '../../APIComponents/AdminApi'
import { toast } from 'react-toastify'
import axiosInstance from '../../../Utils/axiosInstance'

const ActAndSectionMaster = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find(
        (p) => p.page_name === 'Act & Section Master',
      )
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()
  const [actSection, setActSection] = useState([])
  const [loading, setLoading] = useState(false)
  const [addLoading, setAddLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoadingId, setDeleteLoadingId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedDeleteId, setSelectedDeleteId] = useState(null)
  const [selectedActSectionName, setSelectedActSectionName] = useState('')
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedActSection, setSelectedActSection] = useState(null)

  const handleView = (record) => {
    setSelectedActSection(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  //  Fetch All Act Section
  const fetchActSection = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(getActSection)
      setActSection(res.data)
    } catch (err) {
      toast.error('Failed to fetch Act Section')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActSection()
  }, [])

  //  Add or Update act Section
  const onFinish = async (values) => {
    const { actSectionName } = values
    if (!actSectionName) {
      toast.error('Please enter a valid Act-Section name')
      return
    }

    const objData = { act_and_sec: actSectionName }

    try {
      if (editingId) {
        setUpdateLoading(true)
        const res = await axiosInstance.put(
          `${updateActSection}/${editingId}`,
          objData,
        )
        if (res.data == 'UPDATED') {
          toast.success('Act-Section updated successfully')
          fetchActSection()
        }
      } else {
        setAddLoading(true)
        const res = await axiosInstance.post(saveActSection, objData)
        if (res.data == 'SAVED') {
          toast.success('Act-Section added successfully')
          fetchActSection()
        }
      }
      form.resetFields()
      setEditingId(null)
    } catch (err) {
      toast.error('Operation failed. Please try again.')
    } finally {
      setAddLoading(false)
      setUpdateLoading(false)
    }
  }

  //  Edit act Section
  const handleEdit = (record) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    setEditingId(record.id)
    form.setFieldsValue({ actSectionName: record.act_and_sec })
  }

  //  Delete act Section
  const handleDelete = async () => {
    try {
      setDeleteLoadingId(selectedDeleteId)

      const res = await axiosInstance.delete(
        `${deleteActSection}/${selectedDeleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Act-Section deleted successfully')
        fetchActSection()
        setIsDeleteModalOpen(false)
        setSelectedDeleteId(null)
        setSelectedActSectionName('')
      }
    } catch (err) {
      toast.error('Failed to delete Act Section')
    } finally {
      setDeleteLoadingId(null)
    }
  }

  //  Cancel Edit
  const handleCancel = () => {
    setEditingId(null)
    form.resetFields()
  }

  //  Table Columns
  const columns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    {
      title: 'Act Section Name',
      dataIndex: 'act_and_sec',
      key: 'act_and_sec',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: '25%',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    icon={<EditOutlined />}
                    title="Edit"
                    onClick={() => handleEdit(record)}
                  ></Button>
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setSelectedDeleteId(record.id)
                      setSelectedActSectionName(record.act_and_sec)
                      setIsDeleteModalOpen(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row p-0 student-form-title">
            <h3 className="section-title1 text-start mb-2">
              Act & Section Master
            </h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* Act - Section Form */}
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              style={{ marginBottom: '20px' }}
            >
              <Row gutter={16} className="pt-3">
                {/* Act & Section Input */}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label="Act & Section Name"
                    name="actSectionName"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter Act and Section',
                      },
                    ]}
                  >
                    <Input placeholder="Enter Act and Section" />
                  </Form.Item>
                </Col>

                {/* Buttons */}
                <Col
                  xs={24}
                  sm={12}
                  md={8}
                  style={{ display: 'flex', alignItems: 'end' }}
                >
                  <Form.Item>
                    <Space>
                      {!editingId ? (
                        <>
                          {pagePermission.is_new == 1 && (
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              loading={addLoading}
                            >
                              Add
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<EditOutlined />}
                            loading={updateLoading}
                          >
                            Update
                          </Button>
                          <Button
                            onClick={handleCancel}
                            disabled={updateLoading}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            {/* Act Section Table */}
            <Table
              columns={columns}
              dataSource={actSection}
              rowKey="id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: actSection.length,
                onChange: (page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize: pageSize,
                  })
                },
              }}
              bordered
              loading={loading}
              className="shadow rounded custom-table p-0"
              style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
            />
          </div>

        {/* ======== Delete Modal ====== */}
          <Modal
            title="Delete Act & Section"
            open={isDeleteModalOpen}
            onCancel={() => {
              setIsDeleteModalOpen(false)
              setSelectedDeleteId(null)
              setSelectedActSectionName('')
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setIsDeleteModalOpen(false)
                  setSelectedDeleteId(null)
                  setSelectedActSectionName('')
                }}
              >
                Cancel
              </Button>,
              <Button
                key="delete"
                type="primary"
                danger
                loading={deleteLoadingId !== null}
                onClick={handleDelete}
              >
                Delete
              </Button>,
            ]}
          >
            <p>Are you sure you want to delete Act & Section?</p>
          </Modal>

          {/* ======== View Modal ====== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Act & Section Details
              </div>
            }
            width={600}
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            centered
          >
            <div
              style={{
                padding: '15px',
                borderRadius: '8px',
                backgroundColor: '#f5f7fa',
                border: '1px solid #e6e6e6',
              }}
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <span style={{ fontWeight: 600 }}>Act & Section Name :</span>
                </Col>

                <Col span={16}>
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                    }}
                  >
                    {selectedActSection?.act_and_sec}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal>

        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default ActAndSectionMaster
